/**
 * Care Background Agent · frontend/src/components/BrowserStream.tsx
 * Live stream of Care's virtual browser (Xvfb + x11vnc) via noVNC.
 */

import { useEffect, useRef, useState } from "react";
import type { Session } from "../hooks/useSession";

interface Props {
  session: Session | null;
}

declare global {
  interface Window {
    RFB?: new (target: HTMLElement, url: string, opts?: object) => {
      viewOnly: boolean;
      scaleViewport: boolean;
      addEventListener(event: string, handler: (e: Event) => void): void;
      disconnect(): void;
    };
  }
}

export default function BrowserStream({ session }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const rfbRef = useRef<InstanceType<NonNullable<typeof window.RFB>> | null>(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const vncUrl = session?.vnc_url;

  useEffect(() => {
    if (!vncUrl || !containerRef.current) return;

    const wsUrl = vncUrl.replace(/^https:\/\//, "wss://").replace(/^http:\/\//, "ws://");

    let rfb: InstanceType<NonNullable<typeof window.RFB>> | null = null;

    const initNoVNC = () => {
      if (!window.RFB || !containerRef.current) return;
      try {
        rfb = new window.RFB(containerRef.current, wsUrl, {
          wsProtocols: ["binary"],
        });
        rfb.viewOnly = true;
        rfb.scaleViewport = true;

        rfb.addEventListener("connect", () => setConnected(true));
        rfb.addEventListener("disconnect", () => setConnected(false));

        rfbRef.current = rfb;
        setError(null);
      } catch (e) {
        setError(String(e));
      }
    };

    if (window.RFB) {
      initNoVNC();
    } else {
      const script = document.createElement("script");
      script.src = "https://cdnjs.cloudflare.com/ajax/libs/noVNC/1.4.0/core/rfb.js";
      script.type = "module";
      script.onload = initNoVNC;
      script.onerror = () => setError("Failed to load noVNC");
      document.head.appendChild(script);
    }

    return () => {
      rfb?.disconnect();
      rfbRef.current = null;
    };
  }, [vncUrl]);

  const isAvailable = !!vncUrl && (session?.status === "active" || session?.status === "booting");

  if (!isAvailable) {
    return (
      <div className="flex flex-col h-full items-center justify-center bg-[#0f1117] text-[#8b8fa8]">
        {session?.status === "booting" ? (
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-[#5b4dff] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm">Browser stream starting…</p>
          </div>
        ) : (
          <div className="text-center">
            <div className="text-3xl mb-3">🌐</div>
            <p className="text-sm">Browser stream unavailable</p>
            <p className="text-xs mt-1 text-[#4a4e65]">Status: {session?.status ?? "none"}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative h-full bg-black">
      {connected && (
        <div className="absolute top-2 left-2 z-10 flex items-center gap-1.5 px-2 py-0.5 bg-black/70 backdrop-blur rounded-full border border-white/10">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
          <span className="text-white text-xs">Live</span>
        </div>
      )}

      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#0f1117] text-red-400 text-xs text-center px-4">
          <p>Stream error: {error}</p>
        </div>
      )}

      <div
        ref={containerRef}
        className="w-full h-full"
        style={{ background: "#1a1a1a" }}
      />

      <div className="absolute bottom-2 right-2 text-[#4a4e65] text-[10px]">
        View only
      </div>
    </div>
  );
}
